import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../services/api";
import { buildEntityTypeAttributesQueryParams } from "../utils/entityTypeAttributes";

const isEmptyValue = (val) => {
  if (val === null || val === undefined) return true;
  if (typeof val === "string" && val.trim() === "") return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (typeof val === "object" && !Array.isArray(val) && Object.keys(val).length === 0)
    return true;
  return false;
};

const formatLabel = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatReadOnlyValue = (attr, raw) => {
  if (raw === null || raw === undefined) return "—";
  if (typeof raw === "string" && raw.trim() === "") return "—";
  if (Array.isArray(raw) && raw.length === 0) return "—";

  const dt = (attr.data_type || "").toLowerCase();
  if (dt === "boolean") {
    return raw === true || raw === "true" ? "Yes" : "No";
  }
  if (dt === "multi_select") {
    return Array.isArray(raw) ? raw.join(", ") : String(raw);
  }
  if (dt === "number" && typeof raw === "number") {
    return String(raw);
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return JSON.stringify(raw, null, 2);
  }
  return String(raw);
};

/**
 * Renders custom attributes using the same admin definitions as the edit form,
 * in read-only mode (all defined fields shown, empty values as —).
 */
const EntityCustomAttributesReadOnly = ({
  entityType,
  entityTypeId = null,
  customAttributes = {},
  /** Optional control rendered beside the section title (e.g. pencil → Edit tab). */
  headingAccessory = null,
  /** When true, omit the title row so the parent can supply one toolbar (e.g. Read inline edit). */
  embedWithoutHeading = false,
  /** When set, only include attributes whose `section` matches (use exact strings from admin). */
  sectionsIncludeOnly = null,
  /** When set, omit attributes in these section names. */
  sectionsExclude = null,
  /** Hide the gray intro line under the heading (narrow section panels). */
  hideIntroParagraph = false,
  /** Hide per-section strip headers (parent already shows section title). */
  omitSectionHeaders = false,
}) => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(!!entityType);

  useEffect(() => {
    if (!entityType) {
      setAttributes([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const param = buildEntityTypeAttributesQueryParams(
          entityType,
          entityTypeId
        );
        if (!param) {
          setAttributes([]);
          return;
        }
        const response = await apiFetch(`/api/admin/entity-type-attributes?${param}`);
        if (cancelled) return;
        if (response?.ok) {
          const data = await response.json();
          setAttributes(Array.isArray(data) ? data : []);
        } else {
          setAttributes([]);
        }
      } catch {
        if (!cancelled) setAttributes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [entityType, entityTypeId]);

  const { groupedAttributes, sortedSections } = useMemo(() => {
    let list = attributes;
    if (sectionsIncludeOnly?.length) {
      const allow = new Set(sectionsIncludeOnly);
      list = list.filter((a) => allow.has(a.section || "General"));
    }
    if (sectionsExclude?.length) {
      const deny = new Set(sectionsExclude);
      list = list.filter((a) => !deny.has(a.section || "General"));
    }
    const groups = list.reduce((acc, attr) => {
      const section = attr.section || "General";
      if (!acc[section]) acc[section] = [];
      acc[section].push(attr);
      return acc;
    }, {});
    Object.keys(groups).forEach((section) => {
      groups[section].sort(
        (a, b) =>
          a.sort_order - b.sort_order ||
          (a.attribute_label || "").localeCompare(b.attribute_label || "")
      );
    });
    return {
      groupedAttributes: groups,
      sortedSections: Object.keys(groups).sort(),
    };
  }, [attributes, sectionsIncludeOnly, sectionsExclude]);

  if (!entityType) {
    return null;
  }

  const headingRow = embedWithoutHeading ? null : (
    <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-700 pb-1">
      <h2 className="text-lg font-semibold text-white">Custom Attributes</h2>
      {headingAccessory}
    </div>
  );

  if (loading) {
    return (
      <section>
        {headingRow}
        <div className="text-gray-400 text-sm py-4">Loading attribute definitions…</div>
      </section>
    );
  }

  if (
    attributes.length > 0 &&
    sortedSections.length === 0 &&
    (sectionsIncludeOnly?.length || sectionsExclude?.length)
  ) {
    return null;
  }

  if (attributes.length === 0) {
    const entries = Object.entries(customAttributes || {}).filter(
      ([, v]) => !isEmptyValue(v)
    );
    if (entries.length === 0) return null;
    return (
      <section>
        {headingRow}
        <p className="text-sm text-gray-400 mb-4">
          Values stored on the entity (no published attribute definitions for this type).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {entries.map(([key, value]) => (
            <div
              key={key}
              className="bg-gray-800 rounded-lg border border-gray-700 px-3 py-2"
            >
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">
                {formatLabel(key)}
              </div>
              <div className="text-sm text-gray-100 break-words whitespace-pre-wrap">
                {typeof value === "object" && value !== null
                  ? JSON.stringify(value, null, 2)
                  : String(value)}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section>
      {headingRow}
      {!hideIntroParagraph ? (
        <p className="text-sm text-gray-400 mb-4">
          Type-specific fields (same layout as in edit). Empty values show as —.
        </p>
      ) : null}
      <div className="space-y-6">
        {sortedSections.map((section) => (
          <div
            key={section}
            className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700"
          >
            {section !== "General" && !omitSectionHeaders && (
              <div className="bg-gray-750 px-4 py-2 border-b border-gray-700">
                <h3 className="text-base font-medium text-gray-200">{section}</h3>
              </div>
            )}
            <div className="p-4 space-y-4">
              {groupedAttributes[section].map((attr) => {
                const raw = customAttributes[attr.attribute_name];
                return (
                  <div key={attr.id || attr.attribute_name}>
                    <div className="text-sm font-medium text-gray-300 mb-1">
                      {attr.attribute_label}
                      {attr.is_required && <span className="text-red-400 ml-1">*</span>}
                    </div>
                    <div className="text-sm text-gray-100 break-words whitespace-pre-wrap">
                      {formatReadOnlyValue(attr, raw)}
                    </div>
                    {attr.description && (
                      <p className="text-xs text-gray-500 mt-1">{attr.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default EntityCustomAttributesReadOnly;
